import { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdOutlineCheckCircle } from "react-icons/md";
import {
  TbLoader2,
  TbFileInvoice,
  TbDownload,
  TbShieldCheck,
  TbCurrencyRupee,
  TbArrowRight,
  TbCheck,
  TbAlertCircle,
  TbRefresh,
  TbCreditCard,
  TbPercentage,
} from "react-icons/tb";
import { FiPackage, FiTruck } from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../../Utils/axiosInstance";
import { generateBulkInvoicePDF, downloadBlob } from "../../../Utils/Pdf/generateInvoicePDFWeb";
import OTPModal from "./../RequirementsPost/Components/OTPModal";
import { s3Upload } from "../../../Utils/s3Upload";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

// ─── Constants ────────────────────────────────────────────────────────────────
const STEPS = {
  INVOICE: "invoice",
  DECLARATION: "declaration",
  OTP: "otp",
  BREAKDOWN: "breakdown",
  PROCESSING: "processing",
  SUCCESS: "success",
  FAILED: "failed",
};

const RAZORPAY_CDN = "https://checkout.razorpay.com/v1/checkout.js";
const LC_AMOUNT_THRESHOLD = 50000;

const roundAmount = (amount) => {
  const n = Number(amount || 0);
  return n % 1 !== 0 ? Math.round(n * 100) / 100 : Math.round(n);
};

const fmt = (amount) => {
  const n = Number(amount || 0);
  if (isNaN(n)) return "0";
  return n % 1 !== 0
    ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toLocaleString("en-IN");
};

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RAZORPAY_CDN;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// ─── Sub-components ───────────────────────────────────────────────────────────
const Row = ({ label, value, accent }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-50 last:border-0">
    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0 min-w-[130px] pt-0.5">
      {label}
    </span>
    <span
      className={`text-sm font-semibold text-right break-words ${accent ? "text-primaryColor font-extrabold text-base" : "text-gray-800"
        }`}
    >
      {value}
    </span>
  </div>
);

const Divider = () => <div className="h-px bg-gray-100 my-3" />;

const PaymentMethodFlag = ({ method, amount = 0 }) => {
  const razorpayEligible = amount < 50000;

  const methods = {
    razorpay: {
      flag: "🚀 Instant",
      color: "bg-green-100 text-green-700 border-green-300",
      show: razorpayEligible,
    },
    lc: {
      flag: "🐢 Slower",
      color: "bg-blue-100 text-blue-700 border-blue-300",
      show: true,
    },
    bank_transfer: {
      flag: "⏱️ Medium",
      color: "bg-orange-100 text-orange-700 border-orange-300",
      show: true,
    },
  };

  const config = methods[method];
  if (!config || !config.show) return null;

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${config.color}`}
    >
      {config.flag}
    </div>
  );
};

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { id: STEPS.INVOICE, label: "Invoice" },
    { id: STEPS.DECLARATION, label: "Declaration" },
    { id: STEPS.OTP, label: "OTP" },
    { id: STEPS.BREAKDOWN, label: "Payment" },
  ];
  const activeIdx = steps.findIndex((s) => s.id === currentStep);
  return (
    <div className="flex items-center justify-center gap-1 mb-4">
      {steps.map((s, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        return (
          <div key={s.id} className="flex items-center gap-1">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black transition-all duration-300 ${done
                ? "bg-primaryColor text-white"
                : active
                  ? "bg-primaryColor/20 text-primaryColor border-2 border-primaryColor"
                  : "bg-gray-100 text-gray-400"
                }`}
            >
              {done ? <TbCheck size={11} /> : idx + 1}
            </div>
            <span
              className={`text-[9px] font-bold uppercase tracking-wide hidden sm:block ${active
                ? "text-primaryColor"
                : done
                  ? "text-gray-500"
                  : "text-gray-300"
                }`}
            >
              {s.label}
            </span>
            {idx < steps.length - 1 && (
              <div
                className={`w-6 h-px mx-0.5 ${done ? "bg-primaryColor" : "bg-gray-200"
                  }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const BulkProformaInvoiceModal = ({
  open,
  onClose,
  quotations = [],
  order, sampleCart,
  user,
  onSuccess,
}) => {
  const [animate, setAnimate] = useState(false);
  const [step, setStep] = useState(STEPS.INVOICE);
  useLockBodyScroll(open);
  // console.log("Rendering BulkProformaInvoiceModal with quotations:", quotations);

  // PDF state
  const [generating, setGenerating] = useState(false);
  const [proformaUrl, setProformaUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");

  // Declaration
  const [declarationChecked, setDeclarationChecked] = useState(false);

  // OTP
  const [showOtp, setShowOtp] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  // Breakdown — aggregate across all quotations
  const [priceBreakdowns, setPriceBreakdowns] = useState({}); // { quotationId: breakdown }
  const [fetchingBreakdown, setFetchingBreakdown] = useState(false);

  // Payment
  const [submitting, setSubmitting] = useState(false);
  const [failReason, setFailReason] = useState("");

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null); // "razorpay" | "lc" | "bank_transfer"


  const hasGenerated = useRef(false);

  // ── Only merge with unpaid cart items ──────────────────────────────────────
  const mergedQuotations = useMemo(() => {
    return quotations
      .map((quotation) => {
        const cartItem = sampleCart?.items?.find(
          (item) =>
            item?.quotationId?.toString() === quotation?._id?.toString() ||
            item?.quotationId?._id?.toString() === quotation?._id?.toString()
        );

        // Skip if this cart item was already paid
        if (cartItem?.isPaid) {
          return null;
        }

        const sampleTransport = quotation?.transportDetails?.find(
          (t) => t?.charge_is_for === "Sample"
        );
        const qBreakdown = sampleTransport?.breakdown?.breakdown;

        const cartPriceBreakDown = {
          baseAmount: cartItem?.priceBreakDown?.baseAmount ?? qBreakdown?.baseAmount ?? 0,
          gst: cartItem?.priceBreakDown?.gst ?? cartItem?.priceBreakDown?.gstRate ?? qBreakdown?.gstRate ?? 0,
          gstAmount: cartItem?.priceBreakDown?.gstAmount ?? qBreakdown?.gstAmount ?? 0,
          transportationCharges:
            cartItem?.priceBreakDown?.transportationCharges ??
            qBreakdown?.transportationCharges ??
            sampleTransport?.transportationAmount ??
            0,
          commissionDetails:
            cartItem?.priceBreakDown?.commissionDetails ??
            qBreakdown?.commissionDetails ??
            {},
          totalAmount:
            cartItem?.priceBreakDown?.totalAmount ??
            qBreakdown?.totalAmount ??
            0,
        };

        return {
          ...quotation,
          cartPriceBreakDown,
        };
      })
      .filter(Boolean); // Remove null entries
  }, [quotations, sampleCart]);

  // Use mergedQuotations length instead of quotations.length
  const headerQuotationCount = mergedQuotations.length;

  // ── Aggregate totals from ONLY unpaid quotation breakdowns ─────────────────
  const aggregated = useMemo(() => {
    return mergedQuotations.reduce(
      (acc, q) => {
        const bd = q.cartPriceBreakDown || {};
        const sampleTransport = q?.transportDetails?.find(
          (t) => t?.charge_is_for === "Sample"
        );
        const qBreakdown = sampleTransport?.breakdown?.breakdown;

        const baseAmount = Number(bd.baseAmount ?? qBreakdown?.baseAmount ?? 0);
        const gstAmount = Number(bd.gstAmount ?? qBreakdown?.gstAmount ?? 0);
        const gstRate = Number(bd.gst ?? bd.gstRate ?? qBreakdown?.gstRate ?? 0);
        const commissionAmount = Number(
          bd.commissionDetails?.commissionInAmount ??
          qBreakdown?.commissionDetails?.commissionInAmount ??
          0
        );
        const transportAmount = Number(
          bd.transportationCharges ??
          qBreakdown?.transportationCharges ??
          sampleTransport?.transportationAmount ??
          0
        );
        const totalAmount = Number(
          bd.totalAmount ??
          qBreakdown?.totalAmount ??
          (baseAmount + gstAmount + commissionAmount + transportAmount)
        );

        acc.baseAmount += baseAmount;
        acc.gstAmount += gstAmount;
        if (!acc.gstRate) acc.gstRate = gstRate;
        acc.commissionAmount += commissionAmount;
        acc.transport += transportAmount;
        acc.grandTotal += totalAmount;

        if (
          !acc.paymentMethod &&
          (qBreakdown?.paymentDetails?.eligiblePaymentMethod ||
            sampleTransport?.breakdown?.paymentDetails?.eligiblePaymentMethod)
        ) {
          acc.paymentMethod =
            qBreakdown?.paymentDetails?.eligiblePaymentMethod ||
            sampleTransport?.breakdown?.paymentDetails?.eligiblePaymentMethod;
        }

        return acc;
      },
      {
        baseAmount: 0,
        gstAmount: 0,
        commissionAmount: 0,
        transport: 0,
        grandTotal: 0,
        gstRate: 0,
        commissionType: "-",
        commissionValue: 0,
        paymentMethod: null,
      }
    );
  }, [mergedQuotations]);

  // Round aggregated amounts
  aggregated.baseAmount = Math.round(aggregated.baseAmount * 100) / 100;
  aggregated.gstAmount = Math.round(aggregated.gstAmount * 100) / 100;
  aggregated.transport = Math.round(aggregated.transport * 100) / 100;
  aggregated.grandTotal = Math.round(aggregated.grandTotal * 100) / 100;

  // Fallback: if no breakdowns yet, sum from cart items
  const hasBreakdownData = aggregated.grandTotal > 0;

  const paymentMethod = aggregated.paymentMethod;
  const forceLC = aggregated.grandTotal > LC_AMOUNT_THRESHOLD;
  const isLC = forceLC || selectedPaymentMethod === "lc";
  const isBankTransfer = selectedPaymentMethod === "bank_transfer";
  const isRazorpay = !forceLC && selectedPaymentMethod === "razorpay";

  // 2% Razorpay commission when Razorpay payment option is chosen
  const RAZORPAY_COMMISSION_PERCENT = 2;
  const razorpayCommission = isRazorpay
    ? Math.round(((aggregated.grandTotal * RAZORPAY_COMMISSION_PERCENT) / 100) * 100) / 100
    : 0;
  const razorpayPayableAmount = isRazorpay
    ? Math.round((aggregated.grandTotal + razorpayCommission) * 100) / 100
    : aggregated.grandTotal;

  const orderNo = order?.orderNumber || "N/A";
  const createdBy = order?.createdBy || {};
  const buyerName =
    [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ") ||
    "N/A";
  const today = new Date().toLocaleDateString("en-GB");
  const combinedTotal = sampleCart?.finalCartAmount && sampleCart.finalCartAmount > 0
    ? Math.max(0, sampleCart.finalCartAmount - (sampleCart.paidAmount || 0))
    : aggregated.grandTotal;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setTimeout(() => setAnimate(true), 10);
      fetchAllBreakdowns();
      if (!hasGenerated.current) {
        hasGenerated.current = true;
        autoGeneratePDF();
      }
    } else {
      setAnimate(false);
      setTimeout(() => {
        setStep(STEPS.INVOICE);
        setDeclarationChecked(false);
        setProformaUrl("");
        setPdfBlob(null);
        setFailReason("");
        hasGenerated.current = false;
        setSelectedPaymentMethod(null);
      }, 300);
    }
  }, [open]);

  const autoGeneratePDF = async () => {
    setGenerating(true);
    try {
      // Fetch latest order for buyer info
      const orderRes = await axios.get(`/order/get-order-by-id/${order?._id}`);
      const latestOrder = orderRes?.data?.data;

      // const result = await generateBulkInvoicePDF({
      //   order: latestOrder || order,
      //   quotations,
      //   isSampleReq: true,
      //   isProforma: true,
      // });

      const result = await generateBulkInvoicePDF({
        order: latestOrder || order,
        quotations: mergedQuotations,
        isSampleReq: true,
        isProforma: true,
      });

      if (result?.blob) {
        setPdfBlob(result.blob);
        setPdfFileName(result.fileName);
        const { url } = await s3Upload(result.blob, "invoices/proforma");
        setProformaUrl(url);
      } else {
        setProformaUrl("generation_failed");
      }
    } catch (err) {
      console.error("[BulkProformaInvoiceModal] PDF gen error:", err);
      setProformaUrl("generation_failed");
    } finally {
      setGenerating(false);
    }
  };

  const fetchAllBreakdowns = async () => {
    setFetchingBreakdown(true);
    try {
      const results = {};
      await Promise.all(
        quotations.map(async (q) => {
          try {
            const { data } = await axios.get(
              `/price-breakdown/quotation/${q._id}`
            );
            if (data?.success) results[q._id] = data.data;
          } catch (_) { }
        })
      );
      setPriceBreakdowns(results);
    } catch (err) {
      console.error("Breakdown fetch error:", err);
    } finally {
      setFetchingBreakdown(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (submitting) return;
    setAnimate(false);
    setTimeout(onClose, 280);
  };

  const handleDownload = () => {
    if (pdfBlob) downloadBlob(pdfBlob, pdfFileName);
    else toast.error("PDF not ready yet, please wait.");
  };

  const handleProceedToDeclaration = () => {
    if (!proformaUrl || proformaUrl.includes("failed")) {
      toast.error("Invoice is still being prepared, please wait.");
      return;
    }
    setStep(STEPS.DECLARATION);
  };

  const handleDeclarationContinue = async () => {
    if (!declarationChecked) {
      toast.error("Please accept the declaration to continue.");
      return;
    }
    setSendingOtp(true);
    try {
      setStep(STEPS.OTP);
      setShowOtp(true);
    } catch (err) {
      toast.error("Failed to open OTP modal");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpVerified = async () => {
    setShowOtp(false);
    await fetchAllBreakdowns();
    setStep(STEPS.BREAKDOWN);
  };

  const handleLCConfirm = async () => {
    setSubmitting(true);
    setFailReason("");
    try {
      // Finalize quotations first
      await finalizeAllQuotations(null);

      // NEW: Then mark cart items as paid (for LC as well)
      // await markCartItemsAsPaid();
    } catch (err) {
      setFailReason("Failed to confirm LC order. Please contact support.");
      setStep(STEPS.FAILED);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankTransferConfirm = async () => {
    setSubmitting(true);
    setFailReason("");
    try {
      await finalizeAllQuotations("bank_transfer");
    } catch (err) {
      setFailReason(
        "Failed to confirm bank transfer order. Please contact support."
      );
      setStep(STEPS.FAILED);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async () => {
    if (!selectedPaymentMethod && !forceLC) {
      setSelectedPaymentMethod("razorpay");
    }
    setSubmitting(true);
    setFailReason("");
    console.log("handlePay called");

    // Calculate payable amount (including 2% commission if Razorpay)
    const paymentAmount = isRazorpay ? razorpayPayableAmount : aggregated.grandTotal;

    if (!paymentAmount || paymentAmount <= 0) {
      setFailReason("Invalid amount calculated. Please refresh and try again.");
      setStep(STEPS.FAILED);
      setSubmitting(false);
      return;
    }

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        setFailReason("Could not load payment gateway...");
        setStep(STEPS.FAILED);
        setSubmitting(false);
        return;
      }

      let razorpayOrderData;
      try {
        // Use unified initiateDirectPayment with cartId
        const res = await axios.post("/payment/initiate-payment", {
          amount: paymentAmount,
          orderId: order?._id,
          orderType: "Sample",
          // Cart identifiers — backend uses these to resolve quotationIds
          cartId: sampleCart?._id,
          cartNumber: sampleCart?.cartNumber,
        });

        if (!res.data.success) {
          setFailReason(res.data.message || "Failed to create payment order.");
          setStep(STEPS.FAILED);
          setSubmitting(false);
          return;
        }
        razorpayOrderData = res.data.data;
      } catch (err) {
        setFailReason(
          err?.response?.data?.message || "Failed to initiate payment. Please try again."
        );
        setStep(STEPS.FAILED);
        setSubmitting(false);
        return;
      }

      const { orderId: rzpOrderId, razorpayKeyId, orderDetails } = razorpayOrderData;
      setSubmitting(false);
      setStep(STEPS.PROCESSING);

      const options = {
        key: razorpayKeyId,
        amount: orderDetails.amount,
        currency: orderDetails.currency || "INR",
        name: "PCM Exports",
        description: `Sample Order - ${order?.orderNumber} (${quotations.length} quotations)`,
        order_id: rzpOrderId,
        prefill: {
          name: buyerName,
          email: createdBy?.email || "",
          contact: createdBy?.phone || "",
        },
        theme: { color: "#772037" },
        modal: {
          ondismiss: () => {
            setStep(STEPS.BREAKDOWN);
            toast("Payment cancelled", { icon: "ℹ️" });
          },
        },
        handler: async (response) => {
          setStep(STEPS.PROCESSING);
          try {
            // Use unified verifyDirectPayment — no quotationIds needed, backend reads from transaction
            const verifyRes = await axios.post("/payment/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (!verifyRes.data.success) {
              setFailReason(verifyRes.data.message || "Payment verification failed.");
              setStep(STEPS.FAILED);
              return;
            }

            await finalizeAllQuotations(response.razorpay_payment_id);
            // markCartItemsAsPaid is now handled server-side in verifyDirectPayment
            // but we still call it to keep the UI cart state fresh
            // await markCartItemsAsPaid();
          } catch (err) {
            setFailReason(
              err?.response?.data?.message || "Payment verification failed. Contact support."
            );
            setStep(STEPS.FAILED);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        setFailReason(
          resp.error?.description || resp.error?.reason || "Payment was declined."
        );
        setStep(STEPS.FAILED);
      });
      rzp.open();
    } catch (err) {
      setFailReason("An unexpected error occurred. Please try again.");
      setStep(STEPS.FAILED);
      setSubmitting(false);
    }
  };

  const finalizeAllQuotations = async (paymentIdentifier) => {
    try {
      const isLCFlow = paymentIdentifier === null;
      const isBankTransferFlow = paymentIdentifier === "bank_transfer";

      const payload = {
        quotationIds: quotations.map((q) => q._id),
        invoice_details: {
          url: proformaUrl,
          type: "Sample",
        },
        paymentDetails: isLCFlow
          ? { method: "Letter_Of_Credit" }
          : isBankTransferFlow
            ? { method: "Direct_Bank_Transfer" }
            : { method: "Online", razorpay_payment_id: paymentIdentifier },
      };

      const res = await axios.patch(
        "/quotation/bulk-select-quotation",
        payload
      );

      if (res.data?.success) {
        setStep(STEPS.SUCCESS);
        onSuccess?.();
      } else {
        setFailReason(res.data?.message || "Failed to confirm quotations.");
        setStep(STEPS.FAILED);
      }
    } catch (err) {
      setFailReason(
        err?.response?.data?.message ||
        "Failed to finalize orders. Contact support."
      );
      setStep(STEPS.FAILED);
    }
  };

  // ── Computed breakdown totals (from freshly fetched data) ─────────────────
  const freshAggregated = quotations.reduce(
    (acc, q) => {
      const bd = priceBreakdowns[q._id]?.sample?.breakdown;
      if (!bd) return acc;
      acc.baseAmount += Number(bd.baseAmount || 0);
      acc.gstAmount += Number(bd.gstAmount || 0);
      if (!acc.gstRate) {
        acc.gstRate = Number(bd.gstRate || bd.gst || 0);
      }
      acc.commissionAmount += Number(
        bd.commissionDetails?.commissionInAmount || 0
      );
      const t = q.transportDetails?.find((t) => t?.charge_is_for === "Sample");
      acc.transport += Number(
        t?.transportationAmount || bd.transportationCharges || 0
      );
      acc.grandTotal += Number(bd.totalAmount || 0);
      if (!acc.paymentMethod && bd.paymentDetails?.eligiblePaymentMethod) {
        acc.paymentMethod = bd.paymentDetails.eligiblePaymentMethod;
      }
      return acc;
    },
    {
      baseAmount: 0,
      gstAmount: 0,
      commissionAmount: 0,
      transport: 0,
      grandTotal: 0,
      gstRate: 0,
      paymentMethod: null,
    }
  );

  // Use fresh data if available, fall back to props-derived
  const displayTotals =
    Object.keys(priceBreakdowns).length > 0 ? freshAggregated : aggregated;
  const finalPaymentMethod =
    freshAggregated.paymentMethod || aggregated.paymentMethod;
  const finalIsLC = finalPaymentMethod?.toLowerCase() === "manual";
  const paymentDisplayAmount = isRazorpay
    ? razorpayPayableAmount
    : aggregated.grandTotal;

  if (!open) return null;

  if (step === STEPS.OTP) {
    return (
      <OTPModal
        show={showOtp}
        onClose={() => {
          setShowOtp(false);
          setStep(STEPS.DECLARATION);
        }}
        onVerified={handleOtpVerified}
        identifier={user?.phone}
      />
    );
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) handleClose();
      }}
    >
      <div
        className={`relative w-full md:w-[680px] bg-white shadow-2xl md:rounded-3xl rounded-t-3xl max-h-[92vh] flex flex-col transition-all duration-300 ${animate
          ? "scale-100 translate-y-0"
          : "scale-95 md:translate-y-8 translate-y-full"
          }`}
      >
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primaryColor/10 flex items-center justify-center">
              <TbFileInvoice className="text-primaryColor" size={18} />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">
                Sample Order Invoice
              </h2>
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide">
                {headerQuotationCount} Quotation
                {headerQuotationCount !== 1 ? "s" : ""} · Order {orderNo}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {step === STEPS.INVOICE && pdfBlob && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primaryColor/10 border border-primaryColor/20 text-primaryColor text-xs font-bold hover:bg-primaryColor/20 transition"
              >
                <TbDownload size={14} />
                Save PDF
              </button>
            )}
            <button
              onClick={handleClose}
              disabled={submitting}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition disabled:opacity-50"
            >
              <MdClose size={16} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── STEP INDICATOR ── */}
        {[STEPS.INVOICE, STEPS.DECLARATION, STEPS.OTP, STEPS.BREAKDOWN].includes(
          step
        ) && (
            <div className="px-5 pt-4 flex-shrink-0">
              <StepIndicator currentStep={step} />
            </div>
          )}

        {/* ── SCROLLABLE BODY ── */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">

          {/* ══════ STEP: INVOICE ══════ */}
          {step === STEPS.INVOICE && (
            <div className="space-y-4 pt-2">
              {/* Brand Card */}
              <div className="rounded-2xl bg-gradient-to-br from-[#0C3E61] to-[#1a5a8a] p-4 text-white">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.15em] text-white/50 mb-0.5">
                      PROFORMA INVOICE — SAMPLE ORDER
                    </p>
                    <h3 className="text-lg font-extrabold">PCM Exports</h3>
                    <p className="text-[10px] text-white/60 mt-0.5 leading-relaxed">
                      Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg,
                      <br />
                      Bhubaneswar, Odisha(India) — 751009
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="inline-block bg-white/15 border border-white/20 rounded-lg px-2.5 py-1.5">
                      <p className="text-[9px] text-white/50 font-semibold">
                        GSTIN
                      </p>
                      <p className="text-xs font-bold">21BBFPM2533K1ZW</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/15">
                  <div>
                    <p className="text-[9px] text-white/40 font-semibold uppercase">
                      Order No.
                    </p>
                    <p className="text-xs font-bold">{orderNo}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-white/40 font-semibold uppercase">
                      Date
                    </p>
                    <p className="text-xs font-bold">{today}</p>
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-300 border border-amber-400/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {quotations.length} Sample Quotations
                    </span>
                  </div>
                </div>
              </div>

              {/* Buyer Info */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest mb-3">
                  Buyer Information
                </p>
                <Divider />
                <Row label="Buyer Name" value={buyerName} />
                <Row
                  label="Order Number"
                  value={orderNo}
                />
              </div>

              {/* Quotations Summary Table */}
              <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest mb-3">
                  Quotations Included ({mergedQuotations.length})
                </p>
                <Divider />
                {mergedQuotations.map((q, index) => {
                  const breakdown = q.cartPriceBreakDown || {};
                  const baseUnitPrice = q.pricing?.amount || 0;
                  const effUnitPrice = q.pricing?.commissionAdjustedAmount || baseUnitPrice;
                  const commPerUnit = Math.max(0, effUnitPrice - baseUnitPrice);
                  const itemCommission =
                    Number(breakdown?.commissionDetails?.commissionInAmount || 0) ||
                    (commPerUnit > 0 ? commPerUnit * (breakdown?.quantity || 1) : 0);

                  return (
                    <div
                      key={q._id}
                      className="rounded-xl border border-gray-200 p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-primaryColor/10 text-primaryColor text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </div>

                            <div>
                              <h3 className="font-semibold text-gray-900">
                                #{q.quotationUniqueNumber}
                              </h3>

                              <p className="text-xs text-gray-500">
                                {q.currency || "₹"} {fmt(effUnitPrice)} /{" "}
                                {q.standardizedUnit}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-bold text-primaryColor text-lg">
                            ₹{fmt(breakdown?.totalAmount)}
                          </p>

                          <p className="text-xs text-gray-400">
                            incl. all
                          </p>
                        </div>
                      </div>

                      {/* Breakdown */}
                      <div className="mt-3 pt-3 border-t border-dashed border-gray-200 space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">
                            Base Amount
                          </span>
                          <span className="font-medium">
                            ₹{fmt(breakdown?.baseAmount)}
                          </span>
                        </div>

                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">
                            GST ({breakdown?.gst || 0}%)
                          </span>
                          <span className="font-medium">
                            ₹{fmt(breakdown?.gstAmount)}
                          </span>
                        </div>

                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">
                            Transport
                          </span>
                          <span className="font-medium">
                            ₹{fmt(breakdown?.transportationCharges)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-3 pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-700">
                    Combined Total(Including other charges)
                  </p>
                  <p className="text-base font-extrabold text-primaryColor">
                    ₹{fmt(combinedTotal)}
                  </p>
                </div>
              </div>

              {/* Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <p className="text-xs text-amber-800 italic leading-relaxed">
                  * This is a Proforma Invoice covering{" "}
                  {quotations.length} sample quotation
                  {quotations.length !== 1 ? "s" : ""}. Our admin team will
                  review your request and issue a confirmed invoice once
                  verified.
                </p>
              </div>

              {generating && (
                <div className="flex items-center justify-center gap-2 py-3 text-gray-400 text-sm">
                  <TbLoader2 size={16} className="animate-spin" />
                  Generating PDF document...
                </div>
              )}
            </div>
          )}

          {/* ══════ STEP: DECLARATION ══════ */}
          {step === STEPS.DECLARATION && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <TbShieldCheck size={28} className="text-blue-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-extrabold text-gray-900">
                  Buyer Declaration
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Please read and accept the declaration to proceed
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-sm text-gray-700 leading-relaxed space-y-3 max-h-64 overflow-y-auto">
                <p className="font-bold text-gray-900">I hereby declare that:</p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>
                    I, the buyer, have reviewed the Proforma Invoice and confirm
                    that all details including product variety, quantity, pricing,
                    and delivery address are correct.
                  </li>
                  <li>
                    I understand that this is a Proforma Invoice covering{" "}
                    {quotations.length} sample quotation
                    {quotations.length !== 1 ? "s" : ""} and the final Tax
                    Invoice will be issued upon confirmation and processing.
                  </li>
                  <li>
                    I agree to the terms and conditions of PCM Exports including
                    payment terms, delivery timelines, and dispute resolution
                    policies.
                  </li>
                  <li>
                    I confirm that the goods ordered are for legitimate commercial
                    purposes and comply with all applicable laws and regulations.
                  </li>
                  <li>
                    I understand that once the order is confirmed and payment is processed, if due to any unavoidable reasons the order gets cancelled because of the supplier, PCM Exports will not be held responsible for it. It will cancel the order and refund the whole amount.
                  </li>
                  <li>
                    All disputes are subject to Bhubaneswar jurisdiction only.
                  </li>
                </ol>
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={declarationChecked}
                    onChange={(e) => setDeclarationChecked(e.target.checked)}
                    className="w-4 h-4 accent-primaryColor cursor-pointer"
                  />
                </div>
                <span className="text-sm text-gray-700 font-medium group-hover:text-gray-900 transition">
                  I have read, understood, and agree to the above declaration and
                  the Terms & Conditions of PCM Exports.
                </span>
              </label>
            </div>
          )}

          {/* ══════ STEP: BREAKDOWN + PAY ══════ */}
          {step === STEPS.BREAKDOWN && (
            <div className="space-y-4 pt-2">
              <div className="text-center mb-2">
                <h3 className="text-lg font-extrabold text-gray-900">
                  Order Summary
                </h3>
                <p className="text-sm text-gray-500">
                  {quotations.length} quotation
                  {quotations.length !== 1 ? "s" : ""} · Review before payment
                </p>
              </div>

              {fetchingBreakdown ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-400">
                  <TbLoader2 size={28} className="animate-spin text-primaryColor" />
                  <p className="text-sm">Loading price breakdown...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Per-quotation breakdown rows */}
                  {quotations.map((q, idx) => {
                    const bd = priceBreakdowns[q._id]?.sample?.breakdown;
                    const t = q.transportDetails?.find(
                      (t) => t?.charge_is_for === "Sample"
                    );
                    const total = bd
                      ? Number(bd.totalAmount || 0)
                      : Number(q.priceBreakdown?.sample?.breakdown?.totalAmount || 0);
                    // console.log(`Quotation ${q._id} breakdown:`, bd, t, total);
                    return (
                      <div
                        key={q._id}
                        className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden"
                      >
                        <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-primaryColor/10 text-primaryColor text-[10px] font-black flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <p className="text-xs font-bold text-gray-900">
                              #{q.quotationUniqueNumber || q._id?.slice(-6)}
                            </p>
                          </div>
                          <p className="text-sm font-extrabold text-primaryColor">
                            ₹{fmt(total)}
                          </p>
                        </div>
                        {bd && (
                          <div className="px-4 py-2 space-y-1">
                            <div className="flex justify-between text-[11px]">
                              <span className="text-gray-400">
                                Base ({bd.quantity} × ₹{fmt(bd.pricePerKg)})
                              </span>
                              <span className="font-semibold text-gray-700">
                                ₹{fmt(bd.baseAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-gray-400">
                                GST ({bd.gst || bd.gstRate || 0}%)
                              </span>
                              <span className="font-semibold text-gray-700">
                                ₹{fmt(bd.gstAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between text-[11px]">
                              <span className="text-gray-400">Transport</span>
                              <span className="font-semibold text-gray-700">
                                ₹{fmt(bd.transportationCharges || (t?.transportationAmount ?? 0))}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="h-px bg-gray-200 my-1" />

                  {/* Aggregate totals */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <span className="text-gray-400"><FiPackage size={14} /></span>
                      <p className="text-sm font-semibold text-gray-800">
                        Combined Base Price
                      </p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      ₹{fmt(displayTotals.baseAmount)}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <span className="text-gray-400">
                        <TbFileInvoice size={14} />
                      </span>
                      <p className="text-sm font-semibold text-gray-800">
                        GST ({displayTotals.gstRate || displayTotals.gst || aggregated.gstRate || 0}%)
                      </p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      ₹{fmt(displayTotals.gstAmount)}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-gray-50 border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <FiTruck className="text-primaryColor flex-shrink-0" size={14} />
                      <p className="text-sm font-semibold text-gray-800">
                        Total Courier Charges
                      </p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">
                      ₹{fmt(displayTotals.transport)}
                    </p>
                  </div>

                  {/* Razorpay 2% Commission line item */}
                  {isRazorpay && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border bg-amber-50/80 border-amber-200 animate-fadeIn">
                      <div className="flex items-center gap-2.5">
                        <span className="text-amber-600">
                          <TbCreditCard size={15} />
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-amber-900">
                            Razorpay Gateway Commission (2%)
                          </p>
                          <p className="text-xs text-amber-600">
                            2% online payment gateway processing charge
                          </p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-amber-900 flex-shrink-0">
                        + ₹{fmt(razorpayCommission)}
                      </p>
                    </div>
                  )}

                  {/* Grand Total */}
                  <div className="relative rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#772037] to-[#CC234F]" />
                    <div className="relative z-10 flex items-center justify-between gap-3 px-5 py-4">
                      <div>
                        <p className="text-xs text-white/70 font-semibold">
                          {isRazorpay
                            ? "Total Payable (Incl. 2% Razorpay Commission)"
                            : "Grand Total (Including other charges)"}
                        </p>
                        <p className="text-[11px] text-white/40 mt-0.5">
                          {isRazorpay
                            ? `Order ₹${fmt(aggregated.grandTotal)} + ₹${fmt(razorpayCommission)} gateway charge`
                            : `${quotations.length} quotations`}
                        </p>
                      </div>
                      <p className="text-2xl font-extrabold text-white">
                        ₹{fmt(isRazorpay ? razorpayPayableAmount : aggregated.grandTotal)}
                      </p>
                    </div>
                  </div>

                  {/* Payment method selection */}
                  {forceLC ? (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-blue-100 bg-blue-50">
                      <div>
                        <p className="text-sm font-semibold text-blue-800">
                          Payment Method
                        </p>
                        <p className="text-xs text-blue-500 mt-1">
                          Orders above ₹{fmt(LC_AMOUNT_THRESHOLD)} require Letter of Credit
                        </p>
                      </div>
                      <div className="px-3 py-1 rounded-lg text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
                        LC ONLY
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 px-1 mb-2">
                        Select Payment Method
                      </p>
                      <div className="grid grid-cols-1 gap-2.5">
                        {/* RAZORPAY OPTION */}
                        <button
                          type="button"
                          onClick={() => setSelectedPaymentMethod("razorpay")}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${isRazorpay
                            ? "border-primaryColor bg-primaryColor/5"
                            : "border-gray-100 bg-white hover:border-gray-200"
                            }`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isRazorpay
                                ? "border-primaryColor bg-primaryColor"
                                : "border-gray-300"
                                }`}
                            >
                              {isRazorpay && (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900">
                                Razorpay Payment
                              </span>
                              <PaymentMethodFlag method="razorpay" amount={aggregated.grandTotal} />
                              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                +2% Commission
                              </span>
                            </div>
                            <p className="text-xs text-gray-600">
                              Pay via credit/debit card, UPI, or net banking. Instant confirmation.
                            </p>
                          </div>
                          <div className="text-lg">💳</div>
                        </button>

                        {/* LETTER OF CREDIT OPTION */}
                        {/* <button
                          type="button"
                          onClick={() => setSelectedPaymentMethod("lc")}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${selectedPaymentMethod === "lc"
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-100 bg-white hover:border-gray-200"
                            }`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === "lc"
                                ? "border-blue-600 bg-blue-600"
                                : "border-gray-300"
                                }`}
                            >
                              {selectedPaymentMethod === "lc" && (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900">
                                Letter of Credit (LC)
                              </span>
                              <PaymentMethodFlag method="lc" amount={aggregated.grandTotal} />
                            </div>
                            <p className="text-xs text-gray-600">
                              Submit LC document for verification. Best for B2B transactions.
                            </p>
                          </div>
                          <div className="text-lg">💼</div>
                        </button> */}

                        {/* DIRECT BANK TRANSFER OPTION */}
                        {/* <button
                          type="button"
                          onClick={() => setSelectedPaymentMethod("bank_transfer")}
                          className={`flex items-start gap-3 p-3 rounded-xl border-2 transition ${selectedPaymentMethod === "bank_transfer"
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-100 bg-white hover:border-gray-200"
                            }`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPaymentMethod === "bank_transfer"
                                ? "border-orange-600 bg-orange-600"
                                : "border-gray-300"
                                }`}
                            >
                              {selectedPaymentMethod === "bank_transfer" && (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              )}
                            </div>
                          </div>
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900">
                                Direct Bank Transfer
                              </span>
                              <PaymentMethodFlag method="bank_transfer" amount={aggregated.grandTotal} />
                            </div>
                            <p className="text-xs text-gray-600">
                              Transfer to our bank account. Upload receipt for verification.
                            </p>
                          </div>
                          <div className="text-lg">🏦</div>
                        </button> */}
                      </div>
                    </div>
                  )}

                  {/* Payment method info / notice */}
                  {isLC ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <TbShieldCheck size={16} className="text-blue-600" />
                        <p className="text-sm font-bold text-blue-700">
                          Letter of Credit (LC) Process
                        </p>
                      </div>
                      <p className="text-xs text-blue-600 leading-relaxed">
                        These sample orders will proceed through a Letter of Credit (LC).
                        Your LC must be verified by PCM Exports before the supplier is
                        allowed to dispatch the samples.
                      </p>
                      <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                        <li>
                          After clicking <strong>"Confirm via Letter of Credit"</strong>,
                          your quotation(s) will be reserved.
                        </li>
                        <li>
                          Upload your LC along with supporting documents from each
                          quotation's detail view.
                        </li>
                        <li>Our admin team will review and verify the submitted LC.</li>
                        <li>
                          The supplier cannot dispatch until the LC has been verified.
                        </li>
                      </ul>
                    </div>
                  ) : isBankTransfer ? (
                    <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <TbCurrencyRupee size={16} className="text-orange-600" />
                        <p className="text-sm font-bold text-orange-700">
                          Direct Bank Transfer Process
                        </p>
                      </div>
                      <p className="text-xs text-orange-600 leading-relaxed">
                        Complete the payment directly to the official PCM Exports bank
                        account. Your payment must be verified before processing continues.
                      </p>
                      <ul className="text-xs text-orange-600 space-y-1 list-disc list-inside">
                        <li>
                          After clicking <strong>"Confirm & Proceed to Bank Transfer"</strong>,
                          your quotation(s) will be reserved.
                        </li>
                        <li>
                          Upload the payment screenshot / transaction receipt from each
                          quotation's detail view.
                        </li>
                        <li>
                          Provide the transaction details — UTR/Reference Number, payment
                          date, etc.
                        </li>
                        <li>Our admin team will verify the payment before proceeding.</li>
                      </ul>
                    </div>
                  ) : isRazorpay ? (
                    <div className="bg-green-50 border border-green-200 rounded-2xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-green-800 font-semibold text-xs">
                          <TbShieldCheck size={16} className="text-green-600" />
                          <span>Secured by Razorpay · Cards · UPI · Netbanking</span>
                        </div>
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                          +2% Commission
                        </span>
                      </div>
                      <div className="pt-2 border-t border-green-200/60 flex items-center justify-between text-xs text-gray-700">
                        <span>Order Amount: <strong>₹{fmt(aggregated.grandTotal)}</strong></span>
                        <span>Gateway Charge (2%): <strong className="text-amber-700">+₹{fmt(razorpayCommission)}</strong></span>
                        <span>Net Payable: <strong className="text-primaryColor text-sm font-black">₹{fmt(razorpayPayableAmount)}</strong></span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 text-center py-2">
                      Select a payment method above to see details
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══════ STEP: PROCESSING ══════ */}
          {step === STEPS.PROCESSING && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <TbLoader2
                size={44}
                className="text-primaryColor animate-spin"
              />
              <h3 className="text-lg font-extrabold text-gray-900">
                Processing Payment
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                Please complete the payment in the Razorpay window. Do not close
                or refresh this page.
              </p>
            </div>
          )}

          {/* ══════ STEP: SUCCESS ══════ */}
          {step === STEPS.SUCCESS && (
            <div className="flex flex-col items-center justify-center py-12 gap-5">
              <div
                className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"
                style={{
                  animation:
                    "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
                }}
              >
                <MdOutlineCheckCircle size={44} className="text-green-600" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-extrabold text-gray-900">
                  Sample Orders Confirmed! 🎉
                </h3>
                <p className="text-sm text-gray-500 mt-2 max-w-xs">
                  {quotations.length} sample quotation
                  {quotations.length !== 1 ? "s have" : " has"} been confirmed.
                  PCM Exports will dispatch the samples shortly.
                </p>
              </div>
              <div
                className={`w-full rounded-2xl p-4 text-center ${isLC
                  ? "bg-blue-50 border border-blue-200"
                  : isBankTransfer
                    ? "bg-orange-50 border border-orange-200"
                    : "bg-green-50 border border-green-200"
                  }`}
              >
                <p
                  className={`text-xs font-semibold ${isLC
                    ? "text-blue-700"
                    : isBankTransfer
                      ? "text-orange-700"
                      : "text-green-700"
                    }`}
                >
                  {isLC
                    ? "Orders reserved. Our team will contact you for LC document exchange."
                    : isBankTransfer
                      ? "Orders reserved. Please upload the bank transfer proof from each quotation's detail view."
                      : "Proforma Invoice has been saved to all selected quotations."}
                </p>
              </div>
            </div>
          )}

          {/* ══════ STEP: FAILED ══════ */}
          {step === STEPS.FAILED && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div
                className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center"
                style={{
                  animation:
                    "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
                }}
              >
                <TbAlertCircle size={32} className="text-red-500" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-extrabold text-gray-900">
                  Payment Failed
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {failReason ||
                    "Something went wrong. Your order was not placed."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ACTIONS ── */}
        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
          {step === STEPS.INVOICE && (
            <button
              onClick={handleProceedToDeclaration}
              disabled={
                generating ||
                !proformaUrl ||
                proformaUrl.includes("failed")
              }
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-primaryColor text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all duration-200 shadow-lg shadow-primaryColor/30"
            >
              {generating ? (
                <>
                  <TbLoader2 size={16} className="animate-spin" />
                  Preparing Document...
                </>
              ) : (
                <>
                  <TbArrowRight size={16} />
                  Confirm & Continue
                </>
              )}
            </button>
          )}

          {step === STEPS.DECLARATION && (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(STEPS.INVOICE)}
                className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
              >
                Back
              </button>
              <button
                onClick={handleDeclarationContinue}
                disabled={!declarationChecked || sendingOtp}
                className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
              >
                {sendingOtp ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <TbShieldCheck size={16} />
                    I Agree — Send OTP
                  </>
                )}
              </button>
            </div>
          )}

          {step === STEPS.BREAKDOWN &&
            (isLC ? (
              <button
                onClick={handleLCConfirm}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-blue-600/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <TbShieldCheck size={16} />
                    Confirm via Letter of Credit
                  </>
                )}
              </button>
            ) : isBankTransfer ? (
              <button
                onClick={handleBankTransferConfirm}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-orange-600 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-orange-600/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Confirming...
                  </>
                ) : (
                  <>
                    <TbCurrencyRupee size={16} />
                    Confirm & Proceed to Bank Transfer
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handlePay}
                disabled={submitting || fetchingBreakdown}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-all shadow-lg shadow-primaryColor/30"
              >
                {submitting ? (
                  <>
                    <TbLoader2 size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <TbCurrencyRupee size={16} />
                    {selectedPaymentMethod === "razorpay"
                      ? `Pay ₹${fmt(razorpayPayableAmount)} via Razorpay`
                      : selectedPaymentMethod === null
                        ? `Select Payment Method`
                        : `Pay ₹${fmt(razorpayPayableAmount)} via Razorpay`}
                  </>
                )}
              </button>
            ))}

          {step === STEPS.SUCCESS && (
            <button
              onClick={handleClose}
              className="w-full py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
            >
              Done
            </button>
          )}

          {step === STEPS.FAILED && (
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setFailReason("");
                  setStep(STEPS.BREAKDOWN);
                }}
                className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-primaryColor text-white rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-primaryColor/30"
              >
                <TbRefresh size={16} />
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { transform: scale(0.4); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default BulkProformaInvoiceModal;